import { useRef, useState } from "react";
import Button from "@mui/material/Button";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { Autocomplete, CircularProgress, Grid, IconButton, List, ListItem, ListItemText, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { IPackage, IPackageWad, wadToString } from "services/registry/PackageService";

export type PkgFormType = "new" | "edit";

export interface PkgFormData {
  id: string;
  wads: IPackageWad[];
  description: string;
}

export interface PkgFormProps {
  visible: boolean;
  type: PkgFormType;
  initialData: IPackage | undefined;
  onDismiss: () => void;
  onAccept: (data: PkgFormData) => Promise<void>;
  wadsFilter: IPackageWad[];
}

export const PackageForm = ({ visible, type, initialData, onDismiss, onAccept, wadsFilter }: PkgFormProps) => {
  const [loading, setLoading] = useState(false);
  const [pkgWads, setPkgWads] = useState<IPackageWad[] | undefined>(initialData?.wads ? [...initialData.wads] : []);
  const [description, setDescription] = useState<string | undefined>(initialData?.description ?? "");
  const refNewWad = useRef<TextFieldProps>(null);

  const dialogCloseHandler = (event: unknown, reason: string) => {
    if (reason !== "backdropClick") {
      onDismiss();
    }
  };

  const okButtonClickHandler = async () => {
    const id = initialData?.id ?? "";
    if (!id) {
      return;
    }

    setLoading(true);
    await onAccept({ id, wads: pkgWads ?? [], description: description ?? "" });
    setLoading(false);
  };

  function removeWad(wadId: string): void {
    const newArray = pkgWads?.filter((p) => p.id !== wadId);
    setPkgWads(newArray ? [...newArray] : []);
  }

  interface WadListProps {
    content: IPackageWad[];
  }
  const WadList = ({ content }: WadListProps) => {
    return (
      <List dense={true}>
        {content && content.length ? (
          content.map((s, idx) => {
            return (
              <ListItem
                key={idx}
                secondaryAction={
                  <IconButton edge="end" aria-label="delete" onClick={() => removeWad(s.id)}>
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText primary={wadToString(s)} />
              </ListItem>
            );
          })
        ) : (
          <ListItem disableGutters>
            <ListItemText primary="No wads to display" />
          </ListItem>
        )}
      </List>
    );
  };

  const addWad = () => {
    if (!refNewWad.current?.value) return;
    const selectedWads = pkgWads ?? [];
    const inputValue = String(refNewWad.current?.value ?? "");
    const [selectedId] = inputValue.split(" | ");
    const newValue = wadsFilter.find((w) => w.id === selectedId.trim());
    if (!newValue) return;

    if (selectedWads.some((wad) => wad.id === newValue.id)) return;

    selectedWads.push(newValue);
    setPkgWads([...selectedWads]);
  };

  return (
    <Dialog open={visible} onClose={dialogCloseHandler} fullWidth maxWidth="sm">
      <DialogTitle>{(type === "new" ? "New" : "Edit") + " Package"}</DialogTitle>
      <DialogContent>
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <DialogContentText>Please fill up the information below:</DialogContentText>
            <Typography component={"div"} variant="body1" color="text.secondary">
              <fieldset>
                <legend>Wads</legend>
                <Grid container alignItems="center" justifyContent={"flex-end"}>
                  <Grid size={10}>
                    <Autocomplete
                      id="wads"
                      freeSolo
                      options={wadsFilter.map((w) => wadToString(w))}
                      renderInput={(params) => <TextField {...params} inputRef={refNewWad} />}
                    />
                  </Grid>
                  <Grid paddingLeft={1} size={2}>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={addWad}>
                      Add
                    </Button>
                  </Grid>
                  <Grid size={12}>
                    <WadList content={pkgWads ?? []} />
                  </Grid>
                </Grid>
              </fieldset>
            </Typography>
            <Typography component={"div"} variant="body1" color="text.secondary">
              <fieldset>
                <legend>Description</legend>
                <TextField
                  id="description"
                  fullWidth
                  multiline
                  rows={4}
                  onChange={(e) => {
                    setDescription(e.target.value);
                  }}
                  value={description}
                />
              </fieldset>
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onDismiss} color="error" disabled={loading}>
          Cancel
        </Button>
        <Button onClick={okButtonClickHandler} disabled={loading}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};
